import React, { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { HelpCircle, Info, Mic, Loader2, ShieldCheck, Star } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../i18n";

export function SectionTitle({ children, sub }) {
  return (
    <div className="mb-3">
      <h2 className="font-head text-xl font-bold tracking-tight text-slate-900">{children}</h2>
      {sub && <p className="text-sm text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

export function Pill({ children, tone = "slate", className = "" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    terracotta: "bg-terracotta-light text-terracotta-dark",
    trust: "bg-trust-light text-trust",
    amber: "bg-amberw-light text-amberw",
    red: "bg-red-50 text-red-600",
  };
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${tones[tone]} ${className}`}>{children}</span>;
}

export function TrustBadge({ trust }) {
  if (!trust) return null;
  return (
    <div className="flex items-center gap-1.5">
      <ShieldCheck className="w-4 h-4 text-trust" />
      <span className="flex">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star key={i} className={`w-3.5 h-3.5 ${i <= trust.stars ? "fill-amberw text-amberw" : "text-slate-300"}`} />
        ))}
      </span>
    </div>
  );
}

/** Plain-language "What does this mean?" explainer that streams from AI */
export function ExplainButton({ term, detail, label, testId }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const openIt = async () => {
    setOpen(true);
    if (text) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/explain-clause", {
        clause_title: term, clause_text: detail || term, language: lang || "en",
      });
      setText(data.explanation);
    } catch {
      setText("Sorry, could not load explanation right now.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openIt}
        data-testid={testId || "explain-clause-modal-btn"}
        className="inline-flex items-center gap-1 text-xs font-semibold text-terracotta hover:text-terracotta-hover"
      >
        <HelpCircle className="w-3.5 h-3.5" /> {label || t("what_means")}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[92vw] sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2 text-left">
              <Info className="w-5 h-5 text-terracotta" /> {term}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 leading-relaxed min-h-[60px]">
            {loading ? <span className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> {t("loading")}</span> : text}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Explain-then-ask banner */
export function WhyBanner({ children }) {
  const { t } = useLang();
  return (
    <div className="flex gap-2 rounded-xl bg-amberw-light border border-amber-200 p-3 text-sm text-amberw">
      <Info className="w-4 h-4 shrink-0 mt-0.5" />
      <div><span className="font-semibold">{t("why_matters")}: </span>{children}</div>
    </div>
  );
}

/** Voice input using Web Speech API, in chosen language */
export function VoiceButton({ onResult, testId = "voice-input-btn" }) {
  const { lang } = useLang();
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const langMap = { hi: "hi-IN", en: "en-IN", mr: "mr-IN", bn: "bn-IN", ta: "ta-IN", te: "te-IN", kn: "kn-IN", gu: "gu-IN", ml: "ml-IN", pa: "pa-IN" };

  const toggle = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Voice input is not supported on this browser."); return; }
    if (listening) { recRef.current?.stop(); return; }
    const rec = new SR();
    rec.lang = langMap[lang] || "en-IN";
    rec.interimResults = false;
    rec.onresult = (e) => onResult(e.results[0][0].transcript);
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid={testId}
      className={`gs-tap flex items-center justify-center rounded-xl px-3 border ${listening ? "bg-terracotta text-white border-terracotta animate-pulse" : "bg-white text-slate-600 border-slate-200"}`}
      title="Speak"
    >
      <Mic className="w-5 h-5" />
    </button>
  );
}

export function EmptyState({ icon: Icon, title, sub, action }) {
  return (
    <div className="gs-card p-8 text-center flex flex-col items-center gap-2">
      {Icon && <Icon className="w-10 h-10 text-slate-300" />}
      <p className="font-head font-semibold text-slate-700">{title}</p>
      {sub && <p className="text-sm text-slate-400">{sub}</p>}
      {action}
    </div>
  );
}

export function PrimaryButton({ children, className = "", ...props }) {
  return (
    <Button
      className={`gs-tap w-full rounded-xl bg-terracotta hover:bg-terracotta-hover text-white font-semibold text-base shadow-sm transition-transform active:scale-[0.98] ${className}`}
      {...props}
    >
      {children}
    </Button>
  );
}
